import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  executeExternalAppContentTarget,
  getExternalAppCalibration,
  launchExternalAppProfile,
  listExternalAppMacros,
  runExternalAppMacro,
  saveExternalAppLaunchRecipe,
  testExternalAppPromptRuleDetection,
  testExternalAppPromptRuleResponse,
} from './externalAppControlApi';

const defaultRecipe = {
  enabled: false,
  launch: { type: 'package', uri: '', fallbackUris: [] },
  timing: {
    coldStartTimeoutMs: 120000,
    initialSettleMs: 1500,
    pollIntervalMs: 3000,
    heartbeatIntervalMs: 10000,
  },
  barrierRuleIds: [],
  ready: { ruleIds: [], match: 'any', consecutiveMatches: 1 },
  postReady: { type: 'none' },
  safety: { maxLaunchAttempts: 1, maxBarrierActions: 8, unexpectedPackage: 'fail' },
};

const wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

const START_POINT_OPTIONS = [
  ['default_playback', 'Default video or channel', 'Navigate to the content this app should normally play.'],
  ['home', 'Known home position', 'Move focus to a repeatable top-level menu position.'],
  ['search', 'Search screen', 'Leave the app focused on its search field for future text entry.'],
  ['epg', 'Programme guide (EPG)', 'Open the app’s programme guide and leave it ready.'],
];

const START_POINT_ACTIONS = [
  ['home', 'Home'], ['back', 'Back'], ['up', '↑'], ['left', '←'],
  ['enter', 'OK'], ['right', '→'], ['down', '↓'],
];

export const mergeLaunchRecipe = (value = {}) => ({
  ...defaultRecipe,
  ...value,
  launch: { ...defaultRecipe.launch, ...(value.launch || {}) },
  timing: { ...defaultRecipe.timing, ...(value.timing || {}) },
  ready: { ...defaultRecipe.ready, ...(value.ready || {}) },
  postReady: { ...defaultRecipe.postReady, ...(value.postReady || {}) },
  safety: { ...defaultRecipe.safety, ...(value.safety || {}) },
  barrierRuleIds: Array.isArray(value.barrierRuleIds) ? value.barrierRuleIds : [],
});

function LaunchRecipePanel({
  session,
  appLabel,
  channelTargets = [],
  disabled,
  onRemoteAction,
  onMessage,
  onError,
}) {
  const [recipe, setRecipe] = useState(() => mergeLaunchRecipe(session?.draftProfile?.launchRecipe));
  const [rules, setRules] = useState([]);
  const [macros, setMacros] = useState({});
  const [busy, setBusy] = useState('');
  const [stages, setStages] = useState([]);
  const cancelledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getExternalAppCalibration(session.id),
      listExternalAppMacros(session.packageName),
    ])
      .then(([latest, savedMacros]) => {
        if (cancelled) return;
        setRules(latest?.promptRules || latest?.draftProfile?.promptRules || []);
        setRecipe(mergeLaunchRecipe(latest?.draftProfile?.launchRecipe));
        setMacros(savedMacros || {});
      })
      .catch(() => onError('Launch recipe configuration could not be refreshed.'));
    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [onError, session.id]);

  const responseRules = useMemo(
    () => rules.filter((rule) => rule.enabled && rule.response?.steps?.length),
    [rules]
  );
  const detectionRules = useMemo(() => rules.filter((rule) => rule.enabled), [rules]);

  const setStage = (name, status, detail) => {
    setStages((current) => [
      ...current.filter((entry) => entry.name !== name),
      { name, status, detail },
    ]);
  };

  const toggleRule = (field, ruleId) => {
    setRecipe((current) => {
      const selected = field === 'barrierRuleIds' ? current.barrierRuleIds : current.ready.ruleIds;
      const next = selected.includes(ruleId)
        ? selected.filter((id) => id !== ruleId)
        : [...selected, ruleId];
      return field === 'barrierRuleIds'
        ? { ...current, barrierRuleIds: next }
        : { ...current, ready: { ...current.ready, ruleIds: next } };
    });
  };

  const refreshOptions = async () => {
    setBusy('refresh');
    onError('');
    try {
      const [latest, savedMacros] = await Promise.all([
        getExternalAppCalibration(session.id),
        listExternalAppMacros(session.packageName),
      ]);
      setRules(latest?.promptRules || latest?.draftProfile?.promptRules || []);
      setMacros(savedMacros || {});
      onMessage('Launch detectors, responses, destinations, and macros refreshed.');
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Launch options could not be refreshed.');
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    onError('');
    try {
      const saved = await saveExternalAppLaunchRecipe(session.id, recipe);
      setRecipe(mergeLaunchRecipe(saved));
      onMessage(`Automatic startup saved for ${appLabel}.`);
      return saved;
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Launch recipe could not be saved.');
      return null;
    } finally {
      setBusy('');
    }
  };

  const runTest = async () => {
    cancelledRef.current = false;
    setBusy('test');
    setStages([]);
    onError('');
    try {
      const saved = await saveExternalAppLaunchRecipe(session.id, { ...recipe, enabled: true });
      setRecipe(mergeLaunchRecipe(saved));
      setStage('Configuration', 'complete', 'Saved and validated');
      setStage(
        'Launch',
        'running',
        saved.launch.type === 'uri' ? 'Opening configured link' : 'Sending app launch command'
      );
      await launchExternalAppProfile(session.profileId);
      if (cancelledRef.current) return;
      setStage('Launch', 'complete', 'Launch command accepted');

      const startedAt = Date.now();
      let barrierActions = 0;
      let consecutiveMatches = 0;
      setStage('Ready state', 'running', 'Waiting for screen evidence');
      while (Date.now() - startedAt < saved.timing.coldStartTimeoutMs) {
        if (cancelledRef.current) return;
        for (const ruleId of saved.barrierRuleIds) {
          if (barrierActions >= saved.safety.maxBarrierActions) break;
          setStage('Launch barriers', 'running', 'Checking for saved startup obstacles');
          const result = await testExternalAppPromptRuleResponse(session.id, ruleId);
          if (result?.actionSent) {
            barrierActions += Math.max(1, result.executedSteps?.length || 0);
            setStage('Launch barriers', 'running', `${barrierActions} bounded action(s) applied`);
          }
        }
        const results = await Promise.all(saved.ready.ruleIds.map((ruleId) =>
          testExternalAppPromptRuleDetection(session.id, ruleId)
        ));
        const matched = saved.ready.match === 'all'
          ? results.every((result) => result?.matched)
          : results.some((result) => result?.matched);
        consecutiveMatches = matched ? consecutiveMatches + 1 : 0;
        if (consecutiveMatches >= saved.ready.consecutiveMatches) break;
        const elapsedSeconds = Math.round((Date.now() - startedAt) / 1000);
        const timeoutSeconds = Math.round(saved.timing.coldStartTimeoutMs / 1000);
        const observedPackage = results
          .map((result) => result?.foregroundPackageName || result?.observedForegroundPackage)
          .find(Boolean);
        setStage(
          'Ready state',
          'running',
          `${elapsedSeconds}s / ${timeoutSeconds}s${observedPackage
            ? ` · currently ${observedPackage}`
            : ''}`
        );
        await wait(saved.timing.pollIntervalMs);
      }
      if (cancelledRef.current) return;
      if (consecutiveMatches < saved.ready.consecutiveMatches) {
        throw new Error(`Ready state was not detected within ${Math.round(saved.timing.coldStartTimeoutMs / 1000)} seconds.`);
      }
      setStage('Ready state', 'complete', 'Configured evidence matched');

      if (saved.postReady.type === 'content_target') {
        setStage('Post-ready action', 'running', 'Opening configured destination');
        await executeExternalAppContentTarget(saved.postReady.contentTargetId);
        setStage('Post-ready action', 'complete', 'Content Target completed');
      } else if (saved.postReady.type === 'macro') {
        const macro = macros[saved.postReady.macroName];
        if (!macro) throw new Error(`Saved macro “${saved.postReady.macroName}” was not found.`);
        setStage('Post-ready action', 'running', `Running macro “${saved.postReady.macroName}”`);
        await runExternalAppMacro({
          deviceId: session.deviceId,
          packageName: session.packageName,
          macroName: saved.postReady.macroName,
          macro,
        });
        setStage('Post-ready action', 'complete', `Macro “${saved.postReady.macroName}” completed`);
      } else if (saved.postReady.type === 'start_point') {
        setStage('Known start point', 'running', 'Replaying recorded navigation');
        for (const step of saved.postReady.steps) {
          await onRemoteAction(step.action);
          await wait(step.delayAfterMs);
        }
        setStage('Known start point', 'complete', 'Recorded start point reached');
      }
      onMessage(`${appLabel} reached its saved ready screen.`);
    } catch (error) {
      const detail = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Recipe test failed';
      setStage('Result', 'failed', detail);
      onError(detail);
    } finally {
      setBusy('');
    }
  };

  const cancelTest = () => {
    cancelledRef.current = true;
    setBusy('');
    setStage('Result', 'cancelled', 'Stopped by operator; no further recipe actions will run');
    onMessage('Automatic startup test cancelled.');
  };

  return (
    <div className="apps-launch-recipe">
      <div className="apps-launch-recipe__heading">
        <div>
          <h4>Automatic startup</h4>
          <p>Choose how to open the app, what a successful start looks like, and what to do next.</p>
        </div>
        <div className="apps-launch-recipe__heading-actions">
          <button type="button" onClick={refreshOptions} disabled={disabled || Boolean(busy)}>
            {busy === 'refresh' ? 'Refreshing…' : 'Refresh Options'}
          </button>
          <label>
            <input
              type="checkbox"
              checked={recipe.enabled}
              onChange={(event) => setRecipe((current) => ({ ...current, enabled: event.target.checked }))}
              disabled={disabled || Boolean(busy)}
            />
            Use automatic startup
          </label>
        </div>
      </div>

      <div className="apps-launch-recipe__grid">
        <label>
          <span>Launch method</span>
          <select
            value={recipe.launch.type}
            onChange={(event) => setRecipe((current) => ({
              ...current,
              launch: { ...current.launch, type: event.target.value },
            }))}
            disabled={disabled || Boolean(busy)}
          >
            <option value="package">Open application</option>
            <option value="uri">Open URI / Intent</option>
          </select>
        </label>
        {recipe.launch.type === 'uri' && (
          <label>
            <span>Launch URI</span>
            <input
              type="text"
              value={recipe.launch.uri}
              onChange={(event) => setRecipe((current) => ({
                ...current,
                launch: { ...current.launch, uri: event.target.value },
              }))}
              placeholder="https://www.youtube.com/playlist?list=…"
              disabled={disabled || Boolean(busy)}
            />
          </label>
        )}
        <label>
          <span>Cold-start timeout (seconds)</span>
          <input
            type="number"
            min="5"
            max="600"
            value={Math.round(recipe.timing.coldStartTimeoutMs / 1000)}
            onChange={(event) => setRecipe((current) => ({
              ...current,
              timing: { ...current.timing, coldStartTimeoutMs: Number(event.target.value) * 1000 },
            }))}
            disabled={disabled || Boolean(busy)}
          />
        </label>
      </div>

      <fieldset disabled={disabled || Boolean(busy)}>
        <legend>Screens that may block startup</legend>
        {responseRules.length ? responseRules.map((rule) => (
          <label key={rule.id}>
            <input
              type="checkbox"
              checked={recipe.barrierRuleIds.includes(rule.id)}
              onChange={() => toggleRule('barrierRuleIds', rule.id)}
            />
            {rule.name}
          </label>
        )) : <p>No startup obstacles saved. Use “Save a Startup Obstacle” above if this app shows profile selection, adverts, or confirmations.</p>}
      </fieldset>

      <fieldset disabled={disabled || Boolean(busy)}>
        <legend>How Suitestream knows the app is ready</legend>
        {detectionRules.length ? detectionRules.map((rule) => (
          <label key={rule.id}>
            <input
              type="checkbox"
              checked={recipe.ready.ruleIds.includes(rule.id)}
              onChange={() => toggleRule('readyRuleIds', rule.id)}
            />
            {rule.name}
          </label>
        )) : <p>No ready screen saved yet. Put the app on its normal home screen, then use “Save Current Screen as Ready” above.</p>}
      </fieldset>

      <div className="apps-start-point-recorder">
        <div>
          <strong>When the app is ready</strong>
          <p>Optionally move the app to one repeatable starting point for later automation.</p>
        </div>
        <select
          value={recipe.postReady.type === 'start_point'
            ? recipe.postReady.kind
            : recipe.postReady.type === 'none' ? 'none' : 'legacy'}
          onChange={(event) => {
            const value = event.target.value;
            setRecipe((current) => ({
              ...current,
              postReady: value === 'none'
                ? { type: 'none' }
                : value === 'legacy'
                  ? current.postReady
                  : { type: 'start_point', kind: value, steps: [] },
            }));
          }}
          disabled={disabled || Boolean(busy)}
        >
          <option value="none">Stop when the ready screen appears</option>
          {START_POINT_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
          {['macro', 'content_target'].includes(recipe.postReady.type) && (
            <option value="legacy">Existing saved action (preserved)</option>
          )}
        </select>
        {recipe.postReady.type === 'start_point' && (
          <div className="apps-start-point-recorder__workspace">
            <p>{START_POINT_OPTIONS.find(([value]) => value === recipe.postReady.kind)?.[2]}</p>
            <p>
              Use the live controls to position the app. Every press below is sent to the TV and
              recorded in order.
            </p>
            <div className="apps-start-point-recorder__buttons">
              {START_POINT_ACTIONS.map(([action, label]) => (
                <button
                  key={action}
                  type="button"
                  onClick={async () => {
                    setBusy(`start-point:${action}`);
                    onError('');
                    try {
                      await onRemoteAction(action);
                      setRecipe((current) => ({
                        ...current,
                        postReady: {
                          ...current.postReady,
                          steps: [...(current.postReady.steps || []), {
                            action,
                            delayAfterMs: 500,
                          }].slice(0, 100),
                        },
                      }));
                    } catch (error) {
                      onError(error.response?.data?.message || error.message || 'Remote command failed.');
                    } finally {
                      setBusy('');
                    }
                  }}
                  disabled={disabled || Boolean(busy)}
                >
                  {busy === `start-point:${action}` ? 'Sending…' : label}
                </button>
              ))}
            </div>
            {recipe.postReady.steps?.length ? (
              <div className="apps-start-point-recorder__sequence">
                <strong>{recipe.postReady.steps.length} recorded presses</strong>
                <span>{recipe.postReady.steps.map((step) => step.action).join(' → ')}</span>
                <button
                  type="button"
                  onClick={() => setRecipe((current) => ({
                    ...current,
                    postReady: { ...current.postReady, steps: current.postReady.steps.slice(0, -1) },
                  }))}
                >
                  Undo last
                </button>
                <button
                  type="button"
                  onClick={() => setRecipe((current) => ({
                    ...current,
                    postReady: { ...current.postReady, steps: [] },
                  }))}
                >
                  Clear
                </button>
              </div>
            ) : (
              <div className="apps-prompt-warning">Record at least one button before saving.</div>
            )}
          </div>
        )}
      </div>

      <div className="apps-launch-recipe__actions">
        <button type="button" onClick={save} disabled={disabled || Boolean(busy)}>
          {busy === 'save' ? 'Saving…' : 'Save Automatic Startup'}
        </button>
        {busy === 'test' ? (
          <button type="button" className="danger" onClick={cancelTest}>Cancel Test</button>
        ) : (
          <button
            type="button"
            className="primary"
            onClick={runTest}
            disabled={disabled || !recipe.ready.ruleIds.length}
          >
            Test Automatic Startup
          </button>
        )}
      </div>

      {stages.length > 0 && (
        <div className="apps-launch-recipe__stages" aria-live="polite">
          {stages.map((stage) => (
            <div key={stage.name} className={`apps-launch-recipe__stage apps-launch-recipe__stage--${stage.status}`}>
              <strong>{stage.name}</strong>
              <span>{stage.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LaunchRecipePanel;
